import { ListGroup, Alert, Spinner } from "react-bootstrap";
import { FaExclamationTriangle } from "react-icons/fa";
import PropTypes from "prop-types";
import PasskeyItem from "./PasskeyItem";

/**
 * List component for displaying multiple passkeys with loading and error states
 * @param {Object} props - Component props
 * @param {Array} props.passkeys - Array of passkey objects to display
 * @param {Function} props.onDelete - Callback function when a passkey is deleted
 * @param {boolean} [props.isLoading=false] - Whether list is in loading state
 * @param {string|null} [props.error=null] - Error message to display if any
 * @returns {JSX.Element} Rendered passkeys list
 */
const PasskeysList = ({
  passkeys,
  onDelete,
  isLoading = false,
  error = null,
}) => {
  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        <div className="d-flex align-items-center">
          <FaExclamationTriangle className="me-3" />
          <div>
            <b>Error loading passkeys</b>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner
          animation="border"
          role="status"
          className="mb-3"
          variant="primary"
        >
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="text-muted mb-0">Loading your passkeys...</p>
      </div>
    );
  }

  if (passkeys.length === 0) {
    return null;
  }

  return (
    <ListGroup variant="flush" className="mt-4 rounded">
      {passkeys.map((passkey) => (
        <PasskeyItem
          key={passkey.id}
          passkey={passkey}
          onDelete={onDelete}
          isLoading={isLoading}
        />
      ))}
    </ListGroup>
  );
};

PasskeysList.propTypes = {
  passkeys: PropTypes.arrayOf(PropTypes.object).isRequired,
  onDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
};

export default PasskeysList;
